package com.dualremotetest

import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sqrt

// Pixel art for the game, drawn for this project in the style of the Famicom original (nothing here is copied from
// the original's CHR graphics, which this project does not have). Colours come from the standard NES 2C02 palette,
// using the colour indices the ROM notes list for each object (for example P1 tank $18/$27/$38). Sprites store
// 2-bit colour indices; index 0 is transparent.

object NesPalette {
    /** A common approximation of the NES 2C02 output palette (ARGB). */
    val RGB = intArrayOf(
        0x7C7C7C, 0x0000FC, 0x0000BC, 0x4428BC, 0x940084, 0xA80020, 0xA81000, 0x881400,
        0x503000, 0x007800, 0x006800, 0x005800, 0x004058, 0x000000, 0x000000, 0x000000,
        0xBCBCBC, 0x0078F8, 0x0058F8, 0x6844FC, 0xD800CC, 0xE40058, 0xF83800, 0xE45C10,
        0xAC7C00, 0x00B800, 0x00A800, 0x00A844, 0x008888, 0x000000, 0x000000, 0x000000,
        0xF8F8F8, 0x3CBCFC, 0x6888FC, 0x9878F8, 0xF878F8, 0xF85898, 0xF87858, 0xFCA044,
        0xF8B800, 0xB8F818, 0x58D854, 0x58F898, 0x00E8D8, 0x787878, 0x000000, 0x000000,
        0xFCFCFC, 0xA4E4FC, 0xB8B8F8, 0xD8B8F8, 0xF8B8F8, 0xF8A4C0, 0xF0D0B0, 0xFCE0A8,
        0xF8D878, 0xD8F878, 0xB8F8B8, 0xB8F8D8, 0x00FCFC, 0xF8D8F8, 0x000000, 0x000000,
    ).map { it or 0xFF000000.toInt() }.toIntArray()

    fun c(index: Int) = RGB[index and 0x3F]

    /** Sub-palette: colours for indices 1..3 (index 0 is transparent). */
    fun pal(a: Int, b: Int, cc: Int) = intArrayOf(0, c(a), c(b), c(cc))

    val BLACK = c(0x0F)
    val GREY = c(0x00)
    val WHITE = c(0x30)
    val RED = c(0x16)
    val ORANGE = c(0x27)

    // Sprite sub-palettes from the ROM (SP0 yellow P1, SP1 green P2, SP2 silver enemies, SP3 red item carriers).
    val P1 = pal(0x18, 0x27, 0x38)
    val P2 = pal(0x0A, 0x1B, 0x3B)
    val ENEMY = pal(0x0C, 0x10, 0x20)
    val FLASH_RED = pal(0x04, 0x16, 0x20)
    val HIT_WHITE = pal(0x10, 0x20, 0x30)
    val ITEM = pal(0x16, 0x10, 0x20)
    val EXPLOSION = pal(0x04, 0x16, 0x30)
    val SPARK = pal(0x0C, 0x21, 0x30)

    // Background sub-palettes from the ROM's in-game set 0 (bricks $17/$06/$00, forest $29/$09/$0B, steel $00/$10/$20).
    val BRICK = pal(0x17, 0x06, 0x00)
    val STEEL = pal(0x00, 0x10, 0x20)
    val FOREST = pal(0x29, 0x09, 0x0B)
    val ICE = pal(0x00, 0x10, 0x20)
    /** Water animates by swapping two colours every 32 frames (ROM palette sets 1 and 2). */
    val WATER_A = pal(0x3C, 0x12, 0x12)
    val WATER_B = pal(0x12, 0x3C, 0x12)
    /** The eagle is a mid-grey emblem in the original (seen in reference screenshots); ROM palette values unverified. */
    val HQ = pal(0x06, 0x10, 0x00)
    val HUD = pal(0x0F, 0x0F, 0x0F)
    val FLAG = pal(0x0F, 0x16, 0x27)
}

class Sprite(val w: Int, val h: Int, val data: ByteArray) {
    operator fun get(x: Int, y: Int) = data[y * w + x].toInt()

    companion object {
        /** Rows of '.', '1', '2', '3' (any other character counts as transparent). */
        fun of(vararg rows: String): Sprite {
            val w = rows[0].length
            require(rows.all { it.length == w }) { "ragged sprite: ${rows.map { it.length }}" }
            val d = ByteArray(w * rows.size)
            rows.forEachIndexed { y, r -> r.forEachIndexed { x, ch -> d[y * w + x] = (if (ch in '1'..'3') ch - '0' else 0).toByte() } }
            return Sprite(w, rows.size, d)
        }

        fun rotate(s: Sprite, d: Dir): Sprite {
            val n = s.w
            val out = ByteArray(n * n)
            for (y in 0 until n) for (x in 0 until n) {
                out[y * n + x] = when (d) {
                    Dir.UP -> s[x, y]
                    Dir.RIGHT -> s[y, n - 1 - x]
                    Dir.DOWN -> s[n - 1 - x, n - 1 - y]
                    Dir.LEFT -> s[n - 1 - y, x]
                }.toByte()
            }
            return Sprite(n, n, out)
        }
    }
}

/** Tank sprite built from simple parts, so all eight designs share one consistent style and rotate cleanly. */
private class TankDesign(
    val treadL: IntRange,
    val treadR: IntRange,
    val treadRows: IntRange,
    val hull: IntArray,
    val turret: IntArray,
    val barrels: List<IntArray>,
    val muzzle: IntArray? = null,
    val plates: Boolean = false,
) {
    fun draw(frame: Int): Sprite {
        val g = Array(16) { IntArray(16) }
        for (y in treadRows) for (x in treadL + treadR) {
            val edge = x == treadL.first || x == treadR.last || x == treadL.last || x == treadR.first
            g[y][x] = if ((y + frame) % 2 == 0) (if (edge) 2 else 3) else 1
        }
        val (hx0, hy0, hx1, hy1) = hull.toList()
        for (y in hy0..hy1) for (x in hx0..hx1) {
            g[y][x] = when {
                y == hy1 || x == hx1 -> 1
                y == hy0 || x == hx0 -> 3
                else -> 2
            }
        }
        if (plates) for (y in hy0 + 2..hy1 - 2 step 2) {
            g[y][hx0 + 1] = 1
            g[y][hx1 - 1] = 1
        }
        val (tx0, ty0, tx1, ty1) = turret.toList()
        for (y in ty0..ty1) for (x in tx0..tx1) {
            val corner = (x == tx0 || x == tx1) && (y == ty0 || y == ty1)
            if (corner) continue
            val border = x == tx0 || x == tx1 || y == ty0 || y == ty1
            g[y][x] = when {
                border -> 1
                (x - tx0) + (y - ty0) <= ((tx1 - tx0) + (ty1 - ty0)) / 2 -> 3
                else -> 2
            }
        }
        for (b in barrels) {
            val (bx0, by0, bx1, by1) = b.toList()
            for (y in by0..by1) for (x in bx0..bx1) g[y][x] = if (x == bx0) 3 else 1
        }
        muzzle?.let { mz ->
            val (mx0, my0, mx1, my1) = mz.toList()
            for (y in my0..my1) for (x in mx0..mx1) g[y][x] = if (y == my1) 1 else 3
        }
        val d = ByteArray(256)
        for (y in 0 until 16) for (x in 0 until 16) d[y * 16 + x] = g[y][x].toByte()
        return Sprite(16, 16, d)
    }
}

object PixelArt {
    private val PLAYER_DESIGNS = listOf(
        TankDesign(1..3, 12..14, 3..15, intArrayOf(4, 4, 11, 14), intArrayOf(5, 6, 10, 11), listOf(intArrayOf(7, 1, 8, 6))),
        TankDesign(1..3, 12..14, 3..15, intArrayOf(4, 4, 11, 14), intArrayOf(5, 5, 10, 11), listOf(intArrayOf(7, 0, 8, 5))),
        TankDesign(1..3, 12..14, 2..15, intArrayOf(4, 3, 11, 14), intArrayOf(5, 5, 10, 12), listOf(intArrayOf(7, 1, 8, 5)), muzzle = intArrayOf(6, 0, 9, 1)),
        TankDesign(0..2, 13..15, 2..15, intArrayOf(3, 3, 12, 14), intArrayOf(5, 5, 10, 12), listOf(intArrayOf(5, 0, 6, 5), intArrayOf(9, 0, 10, 5)), plates = true),
    )
    private val ENEMY_DESIGNS = listOf(
        TankDesign(1..3, 12..14, 4..15, intArrayOf(4, 5, 11, 14), intArrayOf(6, 7, 9, 11), listOf(intArrayOf(7, 2, 8, 7))),
        TankDesign(2..4, 11..13, 2..15, intArrayOf(5, 3, 10, 14), intArrayOf(6, 7, 9, 11), listOf(intArrayOf(7, 0, 8, 7))),
        TankDesign(1..3, 12..14, 3..15, intArrayOf(4, 4, 11, 14), intArrayOf(4, 6, 11, 12), listOf(intArrayOf(7, 1, 8, 6)), muzzle = intArrayOf(6, 0, 9, 1)),
        TankDesign(0..2, 13..15, 2..15, intArrayOf(3, 3, 12, 15), intArrayOf(5, 5, 10, 12), listOf(intArrayOf(7, 0, 8, 5)), plates = true),
    )

    /** [tier][frame][dir] */
    val player: List<List<List<Sprite>>> = PLAYER_DESIGNS.map { d -> (0..1).map { f -> d.draw(f).let { s -> Dir.entries.map { Sprite.rotate(s, it) } } } }
    val enemy: List<List<List<Sprite>>> = ENEMY_DESIGNS.map { d -> (0..1).map { f -> d.draw(f).let { s -> Dir.entries.map { Sprite.rotate(s, it) } } } }

    // 8×8 terrain tiles; each 4×4 terrain cell shows the quarter of the tile it sits on.
    val BRICK = Sprite.of(
        "11131111",
        "11131111",
        "22232222",
        "33333333",
        "11111113",
        "11111113",
        "22222223",
        "33333333",
    )
    val STEEL = Sprite.of(
        "33333331",
        "32222221",
        "32333121",
        "32322121",
        "32322121",
        "32111121",
        "32222221",
        "11111111",
    )
    val WATER = Sprite.of(
        "33333333",
        "31333313",
        "13133131",
        "33313333",
        "33333333",
        "33233332",
        "32323323",
        "23332333",
    )
    val FOREST = Sprite.of(
        "21.12212",
        "1121.121",
        "22113112",
        ".2211322",
        "12.22113",
        "31122.11",
        "1132112.",
        "2.112213",
    )
    val ICE = Sprite.of(
        "22222232",
        "22222322",
        "12223222",
        "21232222",
        "22322221",
        "23222212",
        "32222122",
        "22221222",
    )

    val EAGLE = Sprite.of(
        "................",
        "3..............3",
        "33.....22.....33",
        "333...2332...333",
        ".333..2332..333.",
        ".3333.3223.3333.",
        "..333333333333..",
        "..332333333233..",
        "...3323333233...",
        "....33233233....",
        ".....332233.....",
        "......3333......",
        ".....331133.....",
        "....33.11.33....",
        "...33..11..33...",
        "..333333333333..",
    )
    val EAGLE_DEAD = Sprite.of(
        "................",
        "................",
        "................",
        "................",
        "................",
        "......1.........",
        ".....121....1...",
        "..1..1221..121..",
        "..12112221.1221.",
        ".122122222122221",
        ".122222322222221",
        "1222232222232221",
        "1223222322222321",
        "1222222222222221",
        "1232223222322321",
        "1111111111111111",
    )

    val ITEMS: Map<PowerUpType, Sprite> = mapOf(
        PowerUpType.HELMET to Sprite.of(
            "................",
            "................",
            "......1111......",
            "....11333311....",
            "...1333333331...",
            "..133333333331..",
            "..133332222331..",
            "..133322222231..",
            "..133222222231..",
            ".13332222222231.",
            ".13332222222231.",
            ".11111111111111.",
            "..122222222221..",
            "...1111111111...",
            "................",
            "................",
        ),
        PowerUpType.CLOCK to Sprite.of(
            "................",
            ".....111111.....",
            "...1133333311...",
            "..133333333331..",
            "..133331333331..",
            ".13333313333331.",
            ".13333313333331.",
            ".13333311113331.",
            ".13333333333331.",
            ".13333333333331.",
            "..133333333331..",
            "..133333333331..",
            "...1133333311...",
            ".....111111.....",
            "....11....11....",
            "................",
        ),
        PowerUpType.SHOVEL to Sprite.of(
            "............11..",
            "...........1331.",
            "..........13311.",
            ".........1331...",
            "........1331....",
            ".......1331.....",
            "......1331......",
            ".....1221.......",
            "..1111221.......",
            ".1222221........",
            ".12222221.......",
            ".122222221......",
            ".12222221.......",
            ".1222221........",
            "..11111.........",
            "................",
        ),
        PowerUpType.STAR to Sprite.of(
            "................",
            ".......11.......",
            "......1331......",
            "......1331......",
            ".....133331.....",
            "1111113333111111",
            "1333333333333331",
            ".13333333333331.",
            "..133333333331..",
            "...1333333331...",
            "...1333113331...",
            "..13331..13331..",
            "..1331....1331..",
            ".1331......1331.",
            ".111........111.",
            "................",
        ),
        PowerUpType.GRENADE to Sprite.of(
            "................",
            "......111.......",
            ".....13331......",
            "......111..1....",
            ".....12221.1....",
            "....1222221.....",
            "...122332221....",
            "...123322221....",
            "...122222221....",
            "...122222221....",
            "...122222221....",
            "....1222221.....",
            ".....11111......",
            "................",
            "................",
            "................",
        ),
        PowerUpType.TANK to Sprite.of(
            "................",
            ".......11.......",
            ".......31.......",
            ".111...31...111.",
            ".131.133331.131.",
            ".131133333311311",
            ".13113322233131.",
            ".11113322233111.",
            ".13113322233131.",
            ".13113322233131.",
            ".11113322233111.",
            ".13113333333131.",
            ".13111111111131.",
            ".111........111.",
            "................",
            "................",
        ),
    )

    /** One per star level, in the side panels. */
    val STAR_ICON = Sprite.of(
        "...1....",
        "...1....",
        "..111...",
        "1111111.",
        ".11111..",
        "..111...",
        ".11.11..",
        ".1...1..",
    )
    /** The original's lives icon, drawn in each player's colour (yellow 1P, green 2P). */
    val LIFE_ICON = Sprite.of(
        "...11...",
        "1..11..1",
        "11111111",
        "1.1111.1",
        "11111111",
        "1.1111.1",
        "11111111",
        "1......1",
    )
    val ENEMY_ICON = Sprite.of(
        "1......1",
        "11.11.11",
        "1.1111.1",
        "11111111",
        "1.1111.1",
        "11.11.11",
        "1..11..1",
        "...11...",
    )
    val FLAG = Sprite.of(
        "11..............",
        "11222222222.....",
        "112222222223....",
        "1122222222223...",
        "11222222222223..",
        "1122222222223...",
        "112222222223....",
        "11222222222.....",
        "11..............",
        "11..............",
        "11..............",
        "11..............",
        "11..............",
        "11..............",
        "1111............",
        "1111............",
    )
    val BULLET = Sprite.of(
        ".3.",
        "323",
        "323",
    )

    /** Spawn sparkle, 4 sizes. */
    val SPARK: List<Sprite> = (1..4).map { r -> sparkle(r) }
    /** Shield overlay, 2 frames. */
    val SHIELD: List<Sprite> = (0..1).map { shield(it) }
    val SMALL_BOOM: List<Sprite> = listOf(3.5, 5.5, 7.5).mapIndexed { i, r -> boom(16, r, i) }
    val BIG_BOOM: List<Sprite> = listOf(11.0, 15.0).mapIndexed { i, r -> boom(32, r, i + 3) }

    private fun sparkle(r: Int): Sprite {
        val n = 16
        val d = ByteArray(n * n)
        val c = 7.5
        val len = r * 2
        for (y in 0 until n) for (x in 0 until n) {
            val dx = x - c
            val dy = y - c
            val ax = kotlin.math.abs(dx)
            val ay = kotlin.math.abs(dy)
            val v = when {
                ax < 1 && ay < 1 -> 3
                (ax < 1 && ay <= len) || (ay < 1 && ax <= len) -> if (ax + ay < len / 2.0 + 1) 3 else 2
                kotlin.math.abs(ax - ay) < 0.6 && ax <= r -> 1
                else -> 0
            }
            d[y * n + x] = v.toByte()
        }
        return Sprite(n, n, d)
    }

    private fun shield(f: Int): Sprite {
        val n = 16
        val d = ByteArray(n * n)
        for (y in 0 until n) for (x in 0 until n) {
            val dx = x - 7.5
            val dy = y - 7.5
            val r = sqrt(dx * dx + dy * dy)
            if (r < 6.6 || r > 8.2) continue
            val a = ((atan2(dy, dx) / (2 * PI) + 1.0) * 16).toInt() + f
            if (a % 2 == 0) d[y * n + x] = (if ((a / 2) % 2 == 0) 3 else 2).toByte()
        }
        return Sprite(n, n, d)
    }

    private fun boom(n: Int, radius: Double, seed: Int): Sprite {
        val d = ByteArray(n * n)
        val c = (n - 1) / 2.0
        for (y in 0 until n) for (x in 0 until n) {
            val dx = x - c
            val dy = y - c
            val r = sqrt(dx * dx + dy * dy)
            val a = atan2(dy, dx)
            val edge = radius * (0.72 + 0.28 * cos(5 * a + seed * 1.7) * cos(3 * a - seed))
            if (r > edge) continue
            val f = r / edge
            d[y * n + x] = (if (f < 0.4) 3 else if (f < 0.75) 2 else 1).toByte()
        }
        return Sprite(n, n, d)
    }

    /** 8×8 pixel font (our own glyphs), '#' = ink. */
    val FONT: Map<Char, Array<String>> = buildMap {
        fun g(c: Char, vararg rows: String) { put(c, arrayOf(*rows)) }
        g('A', "..###...", ".##.##..", "##...##.", "##...##.", "#######.", "##...##.", "##...##.", "........")
        g('B', "######..", "##...##.", "##...##.", "######..", "##...##.", "##...##.", "######..", "........")
        g('C', "..####..", ".##..##.", "##......", "##......", "##......", ".##..##.", "..####..", "........")
        g('D', "#####...", "##..##..", "##...##.", "##...##.", "##...##.", "##..##..", "#####...", "........")
        g('E', "#######.", "##......", "##......", "######..", "##......", "##......", "#######.", "........")
        g('F', "#######.", "##......", "##......", "######..", "##......", "##......", "##......", "........")
        g('G', "..#####.", ".##.....", "##......", "##..###.", "##...##.", ".##..##.", "..#####.", "........")
        g('H', "##...##.", "##...##.", "##...##.", "#######.", "##...##.", "##...##.", "##...##.", "........")
        g('I', ".######.", "...##...", "...##...", "...##...", "...##...", "...##...", ".######.", "........")
        g('J', "....###.", ".....##.", ".....##.", ".....##.", "##...##.", "##...##.", ".#####..", "........")
        g('K', "##...##.", "##..##..", "##.##...", "####....", "#####...", "##..##..", "##...##.", "........")
        g('L', "##......", "##......", "##......", "##......", "##......", "##......", "#######.", "........")
        g('M', "##...##.", "###.###.", "#######.", "#######.", "##.#.##.", "##...##.", "##...##.", "........")
        g('N', "##...##.", "###..##.", "####.##.", "#######.", "##.####.", "##..###.", "##...##.", "........")
        g('O', ".#####..", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", ".#####..", "........")
        g('P', "######..", "##...##.", "##...##.", "##...##.", "######..", "##......", "##......", "........")
        g('Q', ".#####..", "##...##.", "##...##.", "##...##.", "##.####.", "##..##..", ".####.#.", "........")
        g('R', "######..", "##...##.", "##...##.", "##..###.", "#####...", "##.###..", "##..###.", "........")
        g('S', ".####...", "##..##..", "##......", ".#####..", ".....##.", "##...##.", ".#####..", "........")
        g('T', ".######.", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "........")
        g('U', "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", ".#####..", "........")
        g('V', "##...##.", "##...##.", "##...##.", "###.###.", ".#####..", "..###...", "...#....", "........")
        g('W', "##...##.", "##...##.", "##.#.##.", "#######.", "#######.", "###.###.", "##...##.", "........")
        g('X', "##...##.", "###.###.", ".#####..", "..###...", ".#####..", "###.###.", "##...##.", "........")
        g('Y', ".##..##.", ".##..##.", ".##..##.", "..####..", "...##...", "...##...", "...##...", "........")
        g('Z', "#######.", ".....##.", "....##..", "...##...", "..##....", ".##.....", "#######.", "........")
        g('0', "..###...", ".##.##..", "##...##.", "##...##.", "##...##.", ".##.##..", "..###...", "........")
        g('1', "...##...", "..###...", "...##...", "...##...", "...##...", "...##...", ".######.", "........")
        g('2', ".#####..", "##...##.", ".....##.", "...###..", ".###....", "##......", "#######.", "........")
        g('3', ".#####..", "##...##.", ".....##.", "..####..", ".....##.", "##...##.", ".#####..", "........")
        g('4', "...###..", "..####..", ".##.##..", "##..##..", "#######.", "....##..", "....##..", "........")
        g('5', "######..", "##......", "######..", ".....##.", ".....##.", "##...##.", ".#####..", "........")
        g('6', "..####..", ".##.....", "##......", "######..", "##...##.", "##...##.", ".#####..", "........")
        g('7', "#######.", "##...##.", "....##..", "...##...", "..##....", "..##....", "..##....", "........")
        g('8', ".#####..", "##...##.", "##...##.", ".#####..", "##...##.", "##...##.", ".#####..", "........")
        g('9', ".#####..", "##...##.", "##...##.", ".######.", ".....##.", "....##..", ".####...", "........")
        g('-', "........", "........", "........", ".#####..", "........", "........", "........", "........")
        g('.', "........", "........", "........", "........", "........", "...##...", "...##...", "........")
        g(':', "........", "...##...", "...##...", "........", "...##...", "...##...", "........", "........")
        g('/', "......#.", ".....##.", "....##..", "...##...", "..##....", ".##.....", "#.......", "........")
        g('%', "##...##.", "##..##..", "...##...", "..##....", ".##.....", "##..##..", "#...##..", "........")
        g('!', "...##...", "...##...", "...##...", "...##...", "...##...", "........", "...##...", "........")
        g('?', ".#####..", "##...##.", ".....##.", "...###..", "...##...", "........", "...##...", "........")
        g('+', "........", "...##...", "...##...", ".######.", "...##...", "...##...", "........", "........")
        g('<', "....##..", "...###..", "..####..", ".#####..", "..####..", "...###..", "....##..", "........")
        g('>', "..##....", "..###...", "..####..", "..#####.", "..####..", "..###...", "..##....", "........")
        g(',', "........", "........", "........", "........", "........", "...##...", "...##...", "..##....")
        g('(', "....##..", "...##...", "..##....", "..##....", "..##....", "...##...", "....##..", "........")
        g(')', "..##....", "...##...", "....##..", "....##..", "....##..", "...##...", "..##....", "........")
        g('\'', "...##...", "...##...", "..##....", "........", "........", "........", "........", "........")
        g(' ', "........", "........", "........", "........", "........", "........", "........", "........")
    }

    /** 3×5 digits for the narrow HUD (score, timers) and score pop-ups. */
    val MINI_DIGITS: List<Array<String>> = listOf(
        arrayOf("###", "#.#", "#.#", "#.#", "###"),
        arrayOf(".#.", "##.", ".#.", ".#.", "###"),
        arrayOf("###", "..#", "###", "#..", "###"),
        arrayOf("###", "..#", "###", "..#", "###"),
        arrayOf("#.#", "#.#", "###", "..#", "..#"),
        arrayOf("###", "#..", "###", "..#", "###"),
        arrayOf("###", "#..", "###", "#.#", "###"),
        arrayOf("###", "..#", "..#", ".#.", ".#."),
        arrayOf("###", "#.#", "###", "#.#", "###"),
        arrayOf("###", "#.#", "###", "..#", "###"),
    )
}
